// Wildcat Portfolio — game data
// Same four-phase shape as the other levels: Recon (tag real alt assets) ->
// Planning (clear a prerequisites gate, then build a zero-based sleeve) ->
// Campaign (defend it through a year of alt-asset-specific situations) ->
// Debrief (illustrative returns + save).
//
// All figures are simplified, illustrative estimates for teaching purposes
// — not financial advice, and not a promise of real-world results.

export const SLEEVE_BUDGET = 5000;
export const CRYPTO_CAP_PCT = 5; // never more than 5% of the sleeve

// ---------------------------------------------------------------------------
// STAGE 1 — RECON: five real categories of alternative assets, straight from
// the source material's own summary table. Two universal tags: risk level
// and liquidity.
// ---------------------------------------------------------------------------
export const RISK_OPTIONS = [
  { id: "MODERATE", label: "Moderate" },
  { id: "HIGH", label: "High" },
  { id: "VERY_HIGH", label: "Very High" },
];

export const LIQUIDITY_OPTIONS = [
  { id: "LOW", label: "Low" },
  { id: "MODERATE", label: "Moderate" },
  { id: "HIGH", label: "High" },
];

export const ALT_ASSETS = [
  {
    id: "realestate",
    title: "Real Estate",
    description: "Rental income and long-term appreciation. Selling typically takes 30+ days.",
    correctRisk: "MODERATE",
    correctLiquidity: "LOW",
    note: "Steady income, but you can't turn a house into cash by tomorrow — that's the trade-off for the stability.",
  },
  {
    id: "gold",
    title: "Gold & Silver",
    description: "Physical commodities, historically used as a hedge against inflation and currency risk.",
    correctRisk: "MODERATE",
    correctLiquidity: "MODERATE",
    note: "Not exciting, not free of ups and downs either — but easier to sell than a house.",
  },
  {
    id: "crypto",
    title: "Cryptocurrency",
    description: "Digital assets with high growth potential and high volatility. Trades 24/7.",
    correctRisk: "VERY_HIGH",
    correctLiquidity: "HIGH",
    note: "Easy to buy and sell any time of day — which is exactly what makes panic-selling so tempting when it drops.",
  },
  {
    id: "business",
    title: "Business & Skills",
    description: "Starting a venture, or investing in your own skills to raise your earning power.",
    correctRisk: "HIGH",
    correctLiquidity: "LOW",
    note: "Hard to cash out of a business (or a skill) on short notice, but it's one of the most powerful wealth levers there is.",
  },
  {
    id: "collectibles",
    title: "Collectibles",
    description: "Art, trading cards, and similar niche items. Accessible, but returns aren't predictable like the broader market.",
    correctRisk: "HIGH",
    correctLiquidity: "LOW",
    note: "Finding a buyer at a fair price can take a while — patience is part of the price of admission.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 2 — PLANNING: a prerequisites gate, an investor-profile choice, then
// a zero-based $5,000 "alternative sleeve" across the same five categories.
// ---------------------------------------------------------------------------
export const PREREQUISITES = [
  { id: "debtfree", label: "Debt-free, or actively paying down high-interest debt" },
  { id: "emergency", label: "Fully funded emergency fund — 3 to 6 months of expenses" },
  { id: "income", label: "Career income is stable" },
];

export const INVESTOR_PROFILES = [
  {
    id: "young",
    label: "Young & Aggressive",
    description: "Under 35, long time horizon. More room to lean into growth and volatility.",
    aggressivenessRange: [1.4, 2.6],
  },
  {
    id: "older",
    label: "Older & Aggressive",
    description: "Nearing retirement. Still wants growth, but with far less room to ride out a crash.",
    aggressivenessRange: [1.0, 1.8],
  },
];

// riskWeight is used only to compute a portfolio "aggressiveness" score
// against the chosen investor profile's target range — not shown directly.
export const SLEEVE_CATEGORIES = [
  { id: "realestate", label: "Real Estate", icon: "🏠", riskWeight: 1, baseReturnPct: 8, flavor: "Income + appreciation, illiquid." },
  { id: "gold", label: "Gold & Silver", icon: "🪙", riskWeight: 1, baseReturnPct: 5, flavor: "A modest inflation hedge." },
  { id: "crypto", label: "Crypto", icon: "🪙‍💥", riskWeight: 3, baseReturnPct: 15, flavor: `Capped at ${CRYPTO_CAP_PCT}% of the sleeve — on purpose.` },
  { id: "business", label: "Business & Skills", icon: "💼", riskWeight: 2, baseReturnPct: 6, flavor: "Investing in ventures or your own earning power." },
  { id: "collectibles", label: "Collectibles", icon: "🖼️", riskWeight: 2, baseReturnPct: 10, flavor: "Niche, illiquid, unpredictable." },
];

export const cryptoCapDollars = () => Math.floor((SLEEVE_BUDGET * CRYPTO_CAP_PCT) / 100);

// ---------------------------------------------------------------------------
// STAGE 3 — CAMPAIGN: six situations across the year, one per alt-asset
// theme plus the "good debt" concept.
// ---------------------------------------------------------------------------
export const ENCOUNTERS = [
  {
    id: "e1",
    label: "Asset-Backed Liability",
    text: "Your car needs replacing. You've got a rental property (or the idea of one) generating income.",
    choices: [
      { id: "income-covers", label: "Let a rental-income stream cover the new car payment instead of paying cash", tag: "good", result: "The asset paid for the liability — the whole point of asset-backed spending.", effect: { realEstateBonus: 150 } },
      { id: "drain-fund", label: "Drain the emergency fund to buy the car outright", tag: "bad", result: "That fund existed for exactly this kind of surprise — now it's gone, and the next surprise has nowhere to land.", effect: { realEstatePenalty: 300 } },
    ],
  },
  {
    id: "e2",
    label: "Gold Rush Temptation",
    text: "A friend insists the dollar is about to crash and says to go all-in on gold.",
    choices: [
      { id: "modest", label: "Keep gold as a modest hedge, not a bet", tag: "good", result: "Gold did its job quietly, the way a hedge is supposed to.", effect: { goldBonus: 50 } },
      { id: "all-in", label: "Get swept up and overconcentrate into gold", tag: "bad", result: "Chasing a prediction instead of sticking to the plan rarely pays off as promised.", effect: { goldPenalty: 200 } },
    ],
  },
  {
    id: "e3",
    label: "Crypto Volatility",
    text: "Your crypto position drops 40% in a month — a completely normal month for this asset class.",
    choices: [
      { id: "hold", label: "Stay the course — it was capped at 5% on purpose, for exactly this", tag: "good", result: "It recovered most of the way back over the following months.", effect: { cryptoOutcome: "hold" } },
      { id: "panic", label: "Panic and sell at the bottom", tag: "bad", result: "That locked in the drop permanently, right before the recovery.", effect: { cryptoOutcome: "panic" } },
    ],
  },
  {
    id: "e4",
    label: "The Collector's Offer",
    text: "You need cash sooner than planned. A buyer for your collectibles is hard to find right now, and the only offer on the table is a steep discount.",
    choices: [
      { id: "patient", label: "Wait it out — illiquid was always part of the deal", tag: "good", result: "A fair-price buyer showed up a few months later.", effect: { collectiblesOutcome: "patient" } },
      { id: "discount", label: "Take the lowball offer because you need cash now", tag: "bad", result: "That's the cost of treating an illiquid asset like a liquid one.", effect: { collectiblesOutcome: "discount" } },
    ],
  },
  {
    id: "e5",
    label: "The Business Idea",
    text: "You've got some skill-building budget saved up.",
    choices: [
      { id: "invest", label: "Invest it in a course or certification that raises your earning power", tag: "good", result: "Skills are an asset nobody can repossess.", effect: { businessOutcome: "invest" } },
      { id: "skip", label: "Skip it — the money feels safer just sitting there", tag: "bad", result: "Safe, and quietly a missed opportunity.", effect: { businessOutcome: "skip" } },
    ],
  },
  {
    id: "e6",
    label: "Good Debt, or No Debt?",
    text: "A lender offers financing to expand a rental property at 6% interest. The numbers show the property would cash-flow 9% net — the income clears the cost of the debt with room to spare.",
    choices: [
      { id: "take", label: "Take the debt — the income exceeds the cost", tag: "good", result: "That's \u201cgood debt\u201d in action: borrowing to buy something that pays for itself and then some.", effect: { goodDebtBonus: 250 } },
      { id: "avoid", label: "Pass — no debt, period, even this one", tag: "neutral", result: "A reasonable, conservative call. Not every good opportunity has to be taken.", effect: {} },
    ],
  },
];

// ---------------------------------------------------------------------------
// MATH HELPERS
// ---------------------------------------------------------------------------

export function sumEffect(log, field) {
  return log.reduce((total, entry) => total + (entry.choice.effect?.[field] || 0), 0);
}

export function findEffectValue(log, field) {
  const entry = log.find((l) => l.choice.effect && field in l.choice.effect);
  return entry ? entry.choice.effect[field] : null;
}

export function computeAggressiveness(allocations) {
  const total = Object.values(allocations).reduce((s, v) => s + v, 0);
  if (total === 0) return 0;
  return SLEEVE_CATEGORIES.reduce((sum, cat) => sum + (allocations[cat.id] || 0) * cat.riskWeight, 0) / total;
}
